import React, { useState } from 'react'
import Image from 'next/image'
import styled from 'styled-components'
import { Stack } from '@awazevr/design-system.components.stack'
import { Text } from '@awazevr/design-system.components.text'
import placeholderThumbnail from '../../../public/booking-placeholder.webp'

type XsPropertyCardProps = {
  title: string
  subtitle: string
  /** Real property image. The placeholder shows until it has loaded. */
  imageUrl?: string
}

const THUMBNAIL_SIZE_PX = 48

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.ColorGrey100};
  border-radius: 8px;
  background: #fff;
`

// Explicit pixel sizes: the DS Text scale does not have 16px / 15px. The doubled
// selector beats the DS's own font-size rule.
const Title = styled(Text)`
  && {
    font-size: 16px;
  }
`

const Subtitle = styled(Text)`
  && {
    font-size: 15px;
  }
`

const ThumbnailFrame = styled.div`
  position: relative;
  flex: none;
  width: ${THUMBNAIL_SIZE_PX}px;
  height: ${THUMBNAIL_SIZE_PX}px;
`

// Hidden until `data-loaded="true"`; the placeholder has no such attribute.
const Thumbnail = styled(Image)`
  position: absolute;
  inset: 0;
  border-radius: 4px;
  object-fit: cover;
  transition: opacity 150ms ease;

  &[data-loaded='false'] {
    opacity: 0;
  }
`

// Booking summary the guest recognises. The thumbnail is a static placeholder
// until the real property image has loaded; if it cannot be loaded, the
// placeholder stays.
export const XsPropertyCard = ({
  title,
  subtitle,
  imageUrl,
}: XsPropertyCardProps): React.ReactElement => {
  const [result, setResult] = useState<{ url: string; loaded: boolean } | null>(
    null,
  )
  const matches = result !== null && result.url === imageUrl
  const hasFailed = matches && !result.loaded
  const isLoaded = matches && result.loaded

  return (
    <Card data-testid="xs-property-card">
      <ThumbnailFrame>
        <Thumbnail
          src={placeholderThumbnail}
          alt=""
          width={THUMBNAIL_SIZE_PX}
          height={THUMBNAIL_SIZE_PX}
          data-testid="xs-property-card-placeholder"
          priority
        />
        {imageUrl && !hasFailed && (
          <Thumbnail
            key={imageUrl}
            src={imageUrl}
            alt=""
            width={THUMBNAIL_SIZE_PX}
            height={THUMBNAIL_SIZE_PX}
            unoptimized
            data-loaded={isLoaded}
            data-testid="xs-property-card-image"
            onLoad={() => setResult({ url: imageUrl, loaded: true })}
            onError={() => setResult({ url: imageUrl, loaded: false })}
          />
        )}
      </ThumbnailFrame>
      <Stack spacing={4}>
        <Title sizes={100} type="bold">
          {title}
        </Title>
        <Subtitle sizes={100} color="ColorGrey600">
          {subtitle}
        </Subtitle>
      </Stack>
    </Card>
  )
}
